import React, {Fragment} from "react";
import {css, Interpolation, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {calculateRemSize, gap, hiddenScroll, mr} from "@pg-design/helpers-css-strings";

import {HEADER_CELL_WIDTH, HEADER_CELL_WIDTH_MOBILE} from "../constants/table_sizes";
import {IUseComparisonTableReturnType} from "../hooks/use_comparison_table";
import {useShowScrollButtons} from "../hooks/use_show_scroll_buttons";
import {useTableHeaderSticky} from "../hooks/use_table_header_sticky";
import {IComparisonTableCellHeader} from "../types/IComparisonTableCellHeader";
import {IComparisonTableElement} from "../types/IComparisonTableElement";
import {Cell} from "./cell/Cell";
import {CellHeader} from "./cell/CellHeader";
import {Row} from "./row/Row";
import {RowHeader} from "./row/RowHeader";
import {TableScrollButton} from "./TableScrollButton";

type ITableProps<TTableItemData extends IComparisonTableElement> = IUseComparisonTableReturnType<TTableItemData>["tableProps"];

export interface IComparisonTableProps<TTableItemData extends IComparisonTableElement> extends ITableProps<TTableItemData> {
    cellHeader: IComparisonTableCellHeader<TTableItemData>;
    data: TTableItemData[];
    disabledItemIds?: IComparisonTableElement["id"][];
    headerStyle?: Interpolation<Theme>;
    gap?: Parameters<typeof gap>[0];
    shrinkWidthToData?: boolean;
    hideNavigationOnMobile?: boolean;
    className?: string;
}

export const ComparisonTable = <TTableItemData extends IComparisonTableElement>(props: IComparisonTableProps<TTableItemData>) => {
    const {data, refs, rows, tableSettings, tableNavigation} = props;

    const showScrollButtons = useShowScrollButtons({
        tableRef: refs.tableRef,
        tableRowsRef: refs.tableRowsRef,
        data: props.data
    });

    const {stickyElementRef, elementBeforeStickyRef} = useTableHeaderSticky();

    return (
        <div css={wrapper} className={props.className}>
            <div ref={elementBeforeStickyRef} css={elementBeforeStickyStyle} />
            <div ref={stickyElementRef} css={[headerWrap, props.headerStyle]}>
                <div css={headerFirstCell}>
                    {/*
                        That's a place for the big add button in upcoming sprints
                    */}
                </div>
                <HeaderContent gapValue={props.gap} ref={refs.headerRef}>
                    {data.map((item, index: number) => {
                        const CellHeaderComponent = props.cellHeader;
                        const isDisabled = props.disabledItemIds?.includes(item.id);
                        return (
                            <CellHeader key={item.id} isDisabled={isDisabled}>
                                <CellHeaderComponent item={item} index={index} />
                            </CellHeader>
                        );
                    })}
                </HeaderContent>
                {showScrollButtons && (
                    <Fragment>
                        <div css={[scrollButtonPositioner(true), props.hideNavigationOnMobile && hideOnMobileStyle]}>
                            <TableScrollButton left onClick={tableNavigation.scrollLeft} />
                        </div>
                        <div css={[scrollButtonPositioner(), props.hideNavigationOnMobile && hideOnMobileStyle]}>
                            <TableScrollButton onClick={tableNavigation.scrollRight} />
                        </div>
                    </Fragment>
                )}
            </div>
            <div ref={refs.tableRef} css={table}>
                <div ref={refs.tableRowsRef} css={[tableRows, props.shrinkWidthToData && shrinkRowsWidthToDataStyle]}>
                    {rows.map((row) => (
                        <Row key={row.key}>
                            {(height) => (
                                <Fragment>
                                    <RowHeader headerIconsSettings={tableSettings.headerIcons} height={height} headerDefinition={row.rowHeader} />

                                    {data.map((item, index) => {
                                        const value = row.key in item ? item[row.key as keyof TTableItemData] : null;
                                        const cellCss = props.gap && index < data.length - 1 ? createTableCellCss(props.gap) : undefined;
                                        const isDisabled = props.disabledItemIds?.includes(item.id);

                                        return (
                                            <Cell
                                                isDisabled={isDisabled}
                                                height={height}
                                                key={item.id}
                                                item={item}
                                                value={value}
                                                cellComponent={row.cell}
                                                css={cellCss}
                                            />
                                        );
                                    })}
                                </Fragment>
                            )}
                        </Row>
                    ))}
                </div>
            </div>
        </div>
    );
};

const wrapper = css`
    position: relative;

    @media print {
        width: 100%;
        height: 100%;
    }
`;

const elementBeforeStickyStyle = css`
    height: 1px;
    width: 100%;
    visibility: hidden;
`;

const headerWrap = css`
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
`;

const headerFirstCell = (theme: Theme) => css`
    flex: 0 0 ${HEADER_CELL_WIDTH_MOBILE};

    @media (min-width: ${theme.breakpoints.md}) {
        flex: 0 0 ${HEADER_CELL_WIDTH};
    }
`;

const HeaderContent = styled.div<{gapValue?: Parameters<typeof gap>[0]}>`
    overflow: auto;
    display: flex;
    ${hiddenScroll()}
    ${({gapValue}) => gap(gapValue)};
`;

const shrinkRowsWidthToDataStyle = css`
    width: fit-content;
`;

const table = css`
    overflow: auto;
    overflow-scrolling: unset;
    ${hiddenScroll()}
`;

const tableRows = (theme: Theme) => css`
    display: grid;
    margin-left: ${HEADER_CELL_WIDTH_MOBILE}; // must be equal to the "width" value of the first (header) column
    column-gap: ${calculateRemSize(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        margin-left: ${HEADER_CELL_WIDTH}; // must be equal to the "width" value of the first (header) column
        &:before {
            width: ${HEADER_CELL_WIDTH}; // must be equal to the "width" value of the first (header) column
        }
    }

    /*
      Paint rows
     */
    & > div:nth-of-type(even) {
        background-color: #f2f2f2;

        & > div:first-of-type > span {
            background-color: #f2f2f2;
        }
    }

    & > div:nth-of-type(odd) {
        background-color: #fff;

        & > div:first-of-type > span {
            background-color: #fff;
        }
    }
`;

const createTableCellCss = (gapValue: Parameters<typeof gap>[0]) => css`
    ${mr(gapValue)};
`;

const scrollButtonPositioner = (left?: boolean) => (theme: Theme) => css`
    position: absolute;
    z-index: 10;
    top: 7.4rem;
    right: ${left ? "auto" : "0"};
    left: ${left ? HEADER_CELL_WIDTH_MOBILE : "auto"};
    transform: translateX(${left ? "-50%" : "50%"});

    @media (min-width: ${theme.breakpoints.md}) {
        left: ${left ? HEADER_CELL_WIDTH : "auto"};
    }
`;

const hideOnMobileStyle = (theme: Theme) => css`
    @media (max-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;
import React, {Fragment} from "react";
import {css, Interpolation, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {calculateRemSize, gap, hiddenScroll, mr} from "@pg-design/helpers-css-strings";

import {HEADER_CELL_WIDTH, HEADER_CELL_WIDTH_MOBILE} from "../constants/table_sizes";
import {IUseComparisonTableReturnType} from "../hooks/use_comparison_table";
import {useShowScrollButtons} from "../hooks/use_show_scroll_buttons";
import {useTableHeaderSticky} from "../hooks/use_table_header_sticky";
import {IComparisonTableCellHeader} from "../types/IComparisonTableCellHeader";
import {IComparisonTableElement} from "../types/IComparisonTableElement";
import {Cell} from "./cell/Cell";
import {CellHeader} from "./cell/CellHeader";
import {Row} from "./row/Row";
import {RowHeader} from "./row/RowHeader";
import {TableScrollButton} from "./TableScrollButton";

type ITableProps<TTableItemData extends IComparisonTableElement> = IUseComparisonTableReturnType<TTableItemData>["tableProps"];

export interface IComparisonTableProps<TTableItemData extends IComparisonTableElement> extends ITableProps<TTableItemData> {
    cellHeader: IComparisonTableCellHeader<TTableItemData>;
    data: TTableItemData[];
    disabledItemIds?: IComparisonTableElement["id"][];
    headerStyle?: Interpolation<Theme>;
    gap?: Parameters<typeof gap>[0];
    shrinkWidthToData?: boolean;
    hideNavigationOnMobile?: boolean;
    className?: string;
}

export const ComparisonTable = <TTableItemData extends IComparisonTableElement>(props: IComparisonTableProps<TTableItemData>) => {
    const {data, refs, rows, tableSettings, tableNavigation} = props;

    const showScrollButtons = useShowScrollButtons({
        tableRef: refs.tableRef,
        tableRowsRef: refs.tableRowsRef,
        data: props.data
    });

    const {stickyElementRef, elementBeforeStickyRef} = useTableHeaderSticky();

    return (
        <div css={wrapper} className={props.className}>
            <div ref={elementBeforeStickyRef} css={elementBeforeStickyStyle} />
            <div ref={stickyElementRef} css={[headerWrap, props.headerStyle]}>
                <div css={headerFirstCell}>
                    {/*
                        That's a place for the big add button in upcoming sprints
                    */}
                </div>
                <HeaderContent gapValue={props.gap} ref={refs.headerRef}>
                    {data.map((item, index: number) => {
                        const CellHeaderComponent = props.cellHeader;
                        const isDisabled = props.disabledItemIds?.includes(item.id);
                        return (
                            <CellHeader key={item.id} isDisabled={isDisabled}>
                                <CellHeaderComponent item={item} index={index} />
                            </CellHeader>
                        );
                    })}
                </HeaderContent>
                {showScrollButtons && (
                    <Fragment>
                        <div css={[scrollButtonPositioner(true), props.hideNavigationOnMobile && hideOnMobileStyle]}>
                            <TableScrollButton left onClick={tableNavigation.scrollLeft} />
                        </div>
                        <div css={[scrollButtonPositioner(), props.hideNavigationOnMobile && hideOnMobileStyle]}>
                            <TableScrollButton onClick={tableNavigation.scrollRight} />
                        </div>
                    </Fragment>
                )}
            </div>
            <div ref={refs.tableRef} css={table}>
                <div ref={refs.tableRowsRef} css={[tableRows, props.shrinkWidthToData && shrinkRowsWidthToDataStyle]}>
                    {rows.map((row) => (
                        <Row key={row.key}>
                            {(height) => (
                                <Fragment>
                                    <RowHeader headerIconsSettings={tableSettings.headerIcons} height={height} headerDefinition={row.rowHeader} />

                                    {data.map((item, index) => {
                                        const value = row.key in item ? item[row.key as keyof TTableItemData] : null;
                                        const cellCss = props.gap && index < data.length - 1 ? createTableCellCss(props.gap) : undefined;
                                        const isDisabled = props.disabledItemIds?.includes(item.id);

                                        return (
                                            <Cell
                                                isDisabled={isDisabled}
                                                height={height}
                                                key={item.id}
                                                item={item}
                                                value={value}
                                                cellComponent={row.cell}
                                                css={cellCss}
                                            />
                                        );
                                    })}
                                </Fragment>
                            )}
                        </Row>
                    ))}
                </div>
            </div>
        </div>
    );
};

const wrapper = css`
    position: relative;

    @media print {
        width: 100%;
        height: 100%;
    }
`;

const elementBeforeStickyStyle = css`
    height: 1px;
    width: 100%;
    visibility: hidden;
`;

const headerWrap = css`
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
`;

const headerFirstCell = (theme: Theme) => css`
    flex: 0 0 ${HEADER_CELL_WIDTH_MOBILE};

    @media (min-width: ${theme.breakpoints.md}) {
        flex: 0 0 ${HEADER_CELL_WIDTH};
    }
`;

const HeaderContent = styled.div<{gapValue?: Parameters<typeof gap>[0]}>`
    overflow: auto;
    display: flex;
    ${hiddenScroll()}
    ${({gapValue}) => gap(gapValue)};
`;

const shrinkRowsWidthToDataStyle = css`
    width: fit-content;
`;

const table = css`
    overflow: auto;
    overflow-scrolling: unset;
    ${hiddenScroll()}
`;

const tableRows = (theme: Theme) => css`
    display: grid;
    margin-left: ${HEADER_CELL_WIDTH_MOBILE}; // must be equal to the "width" value of the first (header) column
    column-gap: ${calculateRemSize(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        margin-left: ${HEADER_CELL_WIDTH}; // must be equal to the "width" value of the first (header) column
        &:before {
            width: ${HEADER_CELL_WIDTH}; // must be equal to the "width" value of the first (header) column
        }
    }

    /*
      Paint rows
     */
    & > div:nth-of-type(even) {
        background-color: #f2f2f2;

        & > div:first-of-type > span {
            background-color: #f2f2f2;
        }
    }

    & > div:nth-of-type(odd) {
        background-color: #fff;

        & > div:first-of-type > span {
            background-color: #fff;
        }
    }
`;

const createTableCellCss = (gapValue: Parameters<typeof gap>[0]) => css`
    ${mr(gapValue)};
`;

const scrollButtonPositioner = (left?: boolean) => (theme: Theme) => css`
    position: absolute;
    z-index: 10;
    top: 7.4rem;
    right: ${left ? "auto" : "0"};
    left: ${left ? HEADER_CELL_WIDTH_MOBILE : "auto"};
    transform: translateX(${left ? "-50%" : "50%"});

    @media (min-width: ${theme.breakpoints.md}) {
        left: ${left ? HEADER_CELL_WIDTH : "auto"};
    }
`;

const hideOnMobileStyle = (theme: Theme) => css`
    @media (max-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;
